import { Injectable } from '@angular/core';
import { RestfulEndpoint } from './restful-endpoint.service';
import { Observable, of } from 'rxjs';
import { TeamMemberResponse } from '../models/members.model';

@Injectable({
  providedIn: 'root',
})
export class ShareWithService {
  constructor(private endPoint: RestfulEndpoint) {}

  getProjectMembers(project_id: any): Observable<TeamMemberResponse> {
    return this.endPoint.post('team_members', { project_id });
  }

  /**
   * GET v2/users query params. Only defined values are sent.
   */
  private buildUsersQueryParams(params: {
    page?: number;
    per_page?: number;
    email?: string;
    keyword?: string;
    role?: string;
    username?: string;
    status?: number;
    is_external?: 0 | 1;
    client_id?: string;
  }): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (params.page !== undefined) out['page'] = params.page;
    if (params.per_page !== undefined) out['per_page'] = params.per_page;
    if (params.email !== undefined && params.email !== '') out['email'] = params.email;
    if (params.keyword !== undefined && params.keyword !== '') out['keyword'] = params.keyword;
    if (params.role !== undefined && params.role !== '') out['role'] = params.role;
    if (params.username !== undefined && params.username !== '') out['username'] = params.username;
    if (params.status !== undefined) out['status'] = params.status;
    if (params.is_external !== undefined) out['is_external'] = params.is_external;
    if (params.client_id !== undefined && params.client_id !== '')
      out['client_id'] = params.client_id;
    return out;
  }

  /** RFC-style email regex: local@domain.tld (no consecutive dots). Match => backend may search outside org. */
  private static readonly EMAIL_REGEX =
    /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  private isFullValidEmail(term: string | null | undefined): boolean {
    const t = term != null ? String(term).trim() : '';
    return t.length > 0 && ShareWithService.EMAIL_REGEX.test(t);
  }

  /**
   * Search users via GET v2/users (refactored API).
   * Sends search term as email query param, page=1, per_page=100.
   * Sends is_external=1 only when term matches a valid email regex, otherwise is_external=0 (within org only).
   * Skips the request when term is empty and returns empty data.
   */
  searchUsers(term: any): Observable<any> {
    const searchTerm = term != null ? String(term).trim() : '';
    if (searchTerm === '') {
      return of({ data: [] });
    }
    const params = this.buildUsersQueryParams({
      page: 1,
      per_page: 100,
      email: searchTerm,
      is_external: this.isFullValidEmail(searchTerm) ? 1 : 0,
    });
    return this.endPoint.get('users', params);
  }

  /**
   * Search users via GET v2/users (same as searchUsers).
   * Sends search term as email query param. is_external=1 only when term matches valid email regex.
   * @param term - The search term (email or name) to search for
   * @param options - Optional page, per_page, role, username, status, client_id
   * @returns Observable with users in data array
   */
  searchUsersV2(
    term: string,
    options?: {
      page?: number;
      per_page?: number;
      role?: string;
      username?: string;
      status?: number;
      client_id?: string;
    },
  ): Observable<any> {
    const searchTerm = term != null ? String(term).trim() : '';
    if (searchTerm === '') {
      return of({ data: [] });
    }
    const params = this.buildUsersQueryParams({
      page: options?.page ?? 1,
      per_page: options?.per_page ?? 100,
      email: searchTerm,
      role: options?.role,
      username: options?.username,
      status: options?.status,
      client_id: options?.client_id,
      is_external: this.isFullValidEmail(searchTerm) ? 1 : 0,
    });
    return this.endPoint.get('users', params);
  }

  getRolesGroups(): Observable<{ roles: string[] }> {
    return this.endPoint.post('get_roles_groups', {});
  }

  updateMemberRole(body: any): Observable<any> {
    return this.endPoint.post('update_member_role', body);
  }

  removeMember(id: any, projectId: string): Observable<any> {
    return this.endPoint.post('remove_member', { shared_to: id, project_id: projectId });
  }

  shareProject(body: any): Observable<any> {
    return this.endPoint.post('share_project', body);
  }

  update_member_responsibilities(body: any): Observable<boolean> {
    return this.endPoint.post('update_member_responsibilities', body);
  }
}
